/**
 * IDF Field Mappings for HVAC Components
 *
 * Defines the mapping between HVAC component fields/ports and IDF object fields.
 * These mappings are based on EnergyPlus IDD schema for each object type.
 */

import type { ComponentIDFMapping } from './types';

/**
 * Field mappings for all supported HVAC components
 *
 * Field indices are 0-based and correspond to the IDF field order.
 * The name field is typically at index 0 for most objects.
 */
export const COMPONENT_IDF_MAPPINGS: ComponentIDFMapping[] = [
  // ============================================
  // FANS
  // ============================================
  {
    componentType: 'fan-variable-volume',
    idfClass: 'Fan:VariableVolume',
    nameFieldIndex: 0,
    totalFields: 17,
    fieldMappings: {
      'Fan Total Efficiency': { fieldIndex: 3, defaultValue: '0.7' },
      'Pressure Rise': { fieldIndex: 4, defaultValue: '1000' },
      'Motor Efficiency': { fieldIndex: 6, defaultValue: '0.9' },
      'Motor In Airstream Fraction': { fieldIndex: 7, defaultValue: '1.0' },
    },
    portMappings: [
      { portId: 'air-inlet', fieldIndex: 1, fieldName: 'Air Inlet Node Name' },
      { portId: 'air-outlet', fieldIndex: 2, fieldName: 'Air Outlet Node Name' },
    ],
  },
  {
    componentType: 'fan-constant-volume',
    idfClass: 'Fan:ConstantVolume',
    nameFieldIndex: 0,
    totalFields: 9,
    fieldMappings: {
      'Fan Total Efficiency': { fieldIndex: 3, defaultValue: '0.7' },
      'Pressure Rise': { fieldIndex: 4, defaultValue: '600' },
      'Motor Efficiency': { fieldIndex: 6, defaultValue: '0.9' },
      'Motor In Airstream Fraction': { fieldIndex: 7, defaultValue: '1.0' },
    },
    portMappings: [
      { portId: 'air-inlet', fieldIndex: 1, fieldName: 'Air Inlet Node Name' },
      { portId: 'air-outlet', fieldIndex: 2, fieldName: 'Air Outlet Node Name' },
    ],
  },
  {
    componentType: 'fan-on-off',
    idfClass: 'Fan:OnOff',
    nameFieldIndex: 0,
    totalFields: 13,
    fieldMappings: {
      'Fan Total Efficiency': { fieldIndex: 2, defaultValue: '0.7' },
      'Pressure Rise': { fieldIndex: 3, defaultValue: '300' },
      'Maximum Flow Rate': { fieldIndex: 4, defaultValue: 'autosize' },
      'Motor Efficiency': { fieldIndex: 5, defaultValue: '0.9' },
      'Motor In Airstream Fraction': { fieldIndex: 6, defaultValue: '1.0' },
    },
    portMappings: [
      // Field 1 is Availability Schedule Name, not a node
      { portId: 'air-inlet', fieldIndex: 7, fieldName: 'Air Inlet Node Name' },
      { portId: 'air-outlet', fieldIndex: 8, fieldName: 'Air Outlet Node Name' },
    ],
  },

  // ============================================
  // COOLING COILS
  // ============================================
  {
    componentType: 'coil-cooling-dx-single-speed',
    idfClass: 'Coil:Cooling:DX:SingleSpeed',
    nameFieldIndex: 0,
    totalFields: 18,
    fieldMappings: {
      'Gross Rated Total Cooling Capacity': { fieldIndex: 2, defaultValue: 'autosize' },
      'Gross Rated Sensible Heat Ratio': { fieldIndex: 3, defaultValue: 'autosize' },
      'Gross Rated Cooling COP': { fieldIndex: 4, defaultValue: '3.0' },
      'Rated Air Flow Rate': { fieldIndex: 5, defaultValue: 'autosize' },
    },
    portMappings: [
      // Field 1 is Availability Schedule Name
      { portId: 'air-inlet', fieldIndex: 8, fieldName: 'Air Inlet Node Name' },
      { portId: 'air-outlet', fieldIndex: 9, fieldName: 'Air Outlet Node Name' },
    ],
  },
  {
    componentType: 'coil-cooling-dx-two-speed',
    idfClass: 'Coil:Cooling:DX:TwoSpeed',
    nameFieldIndex: 0,
    totalFields: 20,
    fieldMappings: {
      'High Speed Gross Rated Total Cooling Capacity': { fieldIndex: 3, defaultValue: 'autosize' },
      'High Speed Gross Rated Sensible Heat Ratio': { fieldIndex: 4, defaultValue: 'autosize' },
      'High Speed Gross Rated Cooling COP': { fieldIndex: 5, defaultValue: '3.0' },
      'High Speed Rated Air Flow Rate': { fieldIndex: 6, defaultValue: 'autosize' },
    },
    portMappings: [
      { portId: 'air-inlet', fieldIndex: 1, fieldName: 'Air Inlet Node Name' },
      { portId: 'air-outlet', fieldIndex: 2, fieldName: 'Air Outlet Node Name' },
    ],
  },
  {
    componentType: 'coil-cooling-water',
    idfClass: 'Coil:Cooling:Water',
    nameFieldIndex: 0,
    totalFields: 14,
    fieldMappings: {
      'Design Water Flow Rate': { fieldIndex: 2, defaultValue: 'autosize' },
      'Design Air Flow Rate': { fieldIndex: 3, defaultValue: 'autosize' },
      'Design Inlet Water Temperature': { fieldIndex: 4, defaultValue: '7' },
      'Design Inlet Air Temperature': { fieldIndex: 5, defaultValue: '24' },
    },
    portMappings: [
      { portId: 'water-inlet', fieldIndex: 6, fieldName: 'Water Inlet Node Name' },
      { portId: 'water-outlet', fieldIndex: 7, fieldName: 'Water Outlet Node Name' },
      { portId: 'air-inlet', fieldIndex: 8, fieldName: 'Air Inlet Node Name' },
      { portId: 'air-outlet', fieldIndex: 9, fieldName: 'Air Outlet Node Name' },
    ],
  },

  // ============================================
  // HEATING COILS
  // ============================================
  {
    componentType: 'coil-heating-water',
    idfClass: 'Coil:Heating:Water',
    nameFieldIndex: 0,
    totalFields: 10,
    fieldMappings: {
      'U-Factor Times Area Value': { fieldIndex: 2, defaultValue: 'autosize' },
      'Maximum Water Flow Rate': { fieldIndex: 3, defaultValue: 'autosize' },
      'Rated Inlet Water Temperature': { fieldIndex: 8, defaultValue: '82.2' },
      'Rated Inlet Air Temperature': { fieldIndex: 9, defaultValue: '16.6' },
    },
    portMappings: [
      { portId: 'water-inlet', fieldIndex: 4, fieldName: 'Water Inlet Node Name' },
      { portId: 'water-outlet', fieldIndex: 5, fieldName: 'Water Outlet Node Name' },
      { portId: 'air-inlet', fieldIndex: 6, fieldName: 'Air Inlet Node Name' },
      { portId: 'air-outlet', fieldIndex: 7, fieldName: 'Air Outlet Node Name' },
    ],
  },
  {
    componentType: 'coil-heating-electric',
    idfClass: 'Coil:Heating:Electric',
    nameFieldIndex: 0,
    totalFields: 5,
    fieldMappings: {
      Efficiency: { fieldIndex: 2, defaultValue: '1.0' },
      'Nominal Capacity': { fieldIndex: 3, defaultValue: 'autosize' },
    },
    portMappings: [
      { portId: 'air-inlet', fieldIndex: 1, fieldName: 'Air Inlet Node Name' },
      { portId: 'air-outlet', fieldIndex: 4, fieldName: 'Air Outlet Node Name' },
    ],
  },
  {
    componentType: 'coil-heating-gas',
    idfClass: 'Coil:Heating:Fuel',
    nameFieldIndex: 0,
    totalFields: 9,
    fieldMappings: {
      'Fuel Type': { fieldIndex: 2, defaultValue: 'NaturalGas' },
      'Burner Efficiency': { fieldIndex: 3, defaultValue: '0.8' },
      'Nominal Capacity': { fieldIndex: 4, defaultValue: 'autosize' },
    },
    portMappings: [
      // Field 1 is Availability Schedule Name
      { portId: 'air-inlet', fieldIndex: 5, fieldName: 'Air Inlet Node Name' },
      { portId: 'air-outlet', fieldIndex: 6, fieldName: 'Air Outlet Node Name' },
    ],
  },
  {
    componentType: 'coil-heating-dx-single-speed',
    idfClass: 'Coil:Heating:DX:SingleSpeed',
    nameFieldIndex: 0,
    totalFields: 16,
    fieldMappings: {
      'Gross Rated Heating Capacity': { fieldIndex: 2, defaultValue: 'autosize' },
      'Gross Rated Heating COP': { fieldIndex: 3, defaultValue: '2.75' },
      'Rated Air Flow Rate': { fieldIndex: 4, defaultValue: 'autosize' },
    },
    portMappings: [
      // Field 1 is Availability Schedule Name
      { portId: 'air-inlet', fieldIndex: 7, fieldName: 'Air Inlet Node Name' },
      { portId: 'air-outlet', fieldIndex: 8, fieldName: 'Air Outlet Node Name' },
    ],
  },

  // ============================================
  // AIR HANDLING
  // ============================================
  {
    componentType: 'heat-exchanger-air-to-air',
    idfClass: 'HeatExchanger:AirToAir:SensibleAndLatent',
    nameFieldIndex: 0,
    totalFields: 18,
    fieldMappings: {
      'Nominal Supply Air Flow Rate': { fieldIndex: 2, defaultValue: 'autosize' },
      'Sensible Effectiveness at 100% Heating Air Flow': { fieldIndex: 3, defaultValue: '0.76' },
      'Latent Effectiveness at 100% Heating Air Flow': { fieldIndex: 4, defaultValue: '0.68' },
      'Sensible Effectiveness at 100% Cooling Air Flow': { fieldIndex: 7, defaultValue: '0.76' },
      'Latent Effectiveness at 100% Cooling Air Flow': { fieldIndex: 8, defaultValue: '0.68' },
    },
    portMappings: [
      { portId: 'supply-air-inlet', fieldIndex: 11, fieldName: 'Supply Air Inlet Node Name' },
      { portId: 'supply-air-outlet', fieldIndex: 12, fieldName: 'Supply Air Outlet Node Name' },
      { portId: 'exhaust-air-inlet', fieldIndex: 13, fieldName: 'Exhaust Air Inlet Node Name' },
      { portId: 'exhaust-air-outlet', fieldIndex: 14, fieldName: 'Exhaust Air Outlet Node Name' },
    ],
  },
  {
    componentType: 'humidifier-steam-electric',
    idfClass: 'Humidifier:Steam:Electric',
    nameFieldIndex: 0,
    totalFields: 6,
    fieldMappings: {
      'Rated Capacity': { fieldIndex: 2, defaultValue: 'autosize' },
      'Rated Power': { fieldIndex: 3, defaultValue: 'autosize' },
    },
    portMappings: [
      { portId: 'air-inlet', fieldIndex: 4, fieldName: 'Air Inlet Node Name' },
      { portId: 'air-outlet', fieldIndex: 5, fieldName: 'Air Outlet Node Name' },
    ],
  },
  {
    componentType: 'outdoor-air-system',
    idfClass: 'AirLoopHVAC:OutdoorAirSystem',
    nameFieldIndex: 0,
    totalFields: 4,
    fieldMappings: {
      'Controller Type': { fieldIndex: 1, defaultValue: 'Controller:OutdoorAir' },
    },
    portMappings: [
      // Note: OA system has complex connections, simplified here
      { portId: 'return-air-inlet', fieldIndex: 2, fieldName: 'Outdoor Air Equipment List Name' },
      { portId: 'mixed-air-outlet', fieldIndex: 3, fieldName: 'Availability Manager List Name' },
    ],
  },
  {
    componentType: 'unitary-heat-pump',
    idfClass: 'AirLoopHVAC:UnitaryHeatPump:AirToAir',
    nameFieldIndex: 0,
    totalFields: 21,
    fieldMappings: {
      'Cooling Supply Air Flow Rate': { fieldIndex: 4, defaultValue: 'autosize' },
      'Heating Supply Air Flow Rate': { fieldIndex: 5, defaultValue: 'autosize' },
    },
    portMappings: [
      { portId: 'air-inlet', fieldIndex: 2, fieldName: 'Air Inlet Node Name' },
      { portId: 'air-outlet', fieldIndex: 3, fieldName: 'Air Outlet Node Name' },
    ],
  },
  {
    componentType: 'unitary-system',
    idfClass: 'AirLoopHVAC:UnitarySystem',
    nameFieldIndex: 0,
    totalFields: 40,
    fieldMappings: {
      'Control Type': { fieldIndex: 1, defaultValue: 'Load' },
    },
    portMappings: [
      { portId: 'air-inlet', fieldIndex: 4, fieldName: 'Air Inlet Node Name' },
      { portId: 'air-outlet', fieldIndex: 5, fieldName: 'Air Outlet Node Name' },
    ],
  },

  // ============================================
  // ZONE EQUIPMENT
  // ============================================
  {
    componentType: 'vav-box-reheat',
    idfClass: 'AirTerminal:SingleDuct:VAV:Reheat',
    nameFieldIndex: 0,
    totalFields: 16,
    fieldMappings: {
      'Maximum Air Flow Rate': { fieldIndex: 3, defaultValue: 'autosize' },
      'Zone Minimum Air Flow Input Method': { fieldIndex: 4, defaultValue: 'Constant' },
      'Constant Minimum Air Flow Fraction': { fieldIndex: 5, defaultValue: '0.3' },
      'Damper Heating Action': { fieldIndex: 11, defaultValue: 'Normal' },
    },
    portMappings: [
      { portId: 'air-inlet', fieldIndex: 2, fieldName: 'Air Inlet Node Name' },
      { portId: 'air-outlet', fieldIndex: 1, fieldName: 'Air Outlet Node Name' },
      { portId: 'hw-inlet', fieldIndex: 9, fieldName: 'Hot Water or Steam Inlet Node Name' },
    ],
  },
  {
    componentType: 'vav-box-no-reheat',
    idfClass: 'AirTerminal:SingleDuct:VAV:NoReheat',
    nameFieldIndex: 0,
    totalFields: 9,
    fieldMappings: {
      'Maximum Air Flow Rate': { fieldIndex: 3, defaultValue: 'autosize' },
      'Zone Minimum Air Flow Input Method': { fieldIndex: 4, defaultValue: 'Constant' },
      'Constant Minimum Air Flow Fraction': { fieldIndex: 5, defaultValue: '0.3' },
    },
    portMappings: [
      { portId: 'air-inlet', fieldIndex: 2, fieldName: 'Air Inlet Node Name' },
      { portId: 'air-outlet', fieldIndex: 1, fieldName: 'Air Outlet Node Name' },
    ],
  },
  {
    componentType: 'cav-box',
    idfClass: 'AirTerminal:SingleDuct:ConstantVolume:NoReheat',
    nameFieldIndex: 0,
    totalFields: 5,
    fieldMappings: {
      'Maximum Air Flow Rate': { fieldIndex: 4, defaultValue: 'autosize' },
    },
    portMappings: [
      // Field 1 is Availability Schedule Name, not a node
      { portId: 'air-inlet', fieldIndex: 2, fieldName: 'Air Inlet Node Name' },
      { portId: 'air-outlet', fieldIndex: 3, fieldName: 'Air Outlet Node Name' },
    ],
  },
  {
    componentType: 'diffuser',
    idfClass: 'AirTerminal:SingleDuct:Uncontrolled',
    nameFieldIndex: 0,
    totalFields: 4,
    fieldMappings: {
      'Maximum Air Flow Rate': { fieldIndex: 3, defaultValue: 'autosize' },
    },
    portMappings: [
      { portId: 'air-inlet', fieldIndex: 1, fieldName: 'Inlet Node Name' },
      { portId: 'air-outlet', fieldIndex: 2, fieldName: 'Zone Supply Air Node Name' },
    ],
  },

  // ============================================
  // STRUCTURAL
  // ============================================
  {
    componentType: 'supply-splitter',
    idfClass: 'AirLoopHVAC:ZoneSplitter',
    nameFieldIndex: 0,
    totalFields: 6,
    fieldMappings: {},
    portMappings: [
      { portId: 'air-inlet', fieldIndex: 1, fieldName: 'Inlet Node Name' },
      { portId: 'outlet-1', fieldIndex: 2, fieldName: 'Outlet 1 Node Name' },
      { portId: 'outlet-2', fieldIndex: 3, fieldName: 'Outlet 2 Node Name' },
      { portId: 'outlet-3', fieldIndex: 4, fieldName: 'Outlet 3 Node Name' },
      { portId: 'outlet-4', fieldIndex: 5, fieldName: 'Outlet 4 Node Name' },
    ],
  },
  {
    componentType: 'return-mixer',
    idfClass: 'AirLoopHVAC:ZoneMixer',
    nameFieldIndex: 0,
    totalFields: 6,
    fieldMappings: {},
    portMappings: [
      { portId: 'air-outlet', fieldIndex: 1, fieldName: 'Outlet Node Name' },
      { portId: 'inlet-1', fieldIndex: 2, fieldName: 'Inlet 1 Node Name' },
      { portId: 'inlet-2', fieldIndex: 3, fieldName: 'Inlet 2 Node Name' },
      { portId: 'inlet-3', fieldIndex: 4, fieldName: 'Inlet 3 Node Name' },
      { portId: 'inlet-4', fieldIndex: 5, fieldName: 'Inlet 4 Node Name' },
    ],
  },
  {
    componentType: 'return-plenum',
    idfClass: 'AirLoopHVAC:ReturnPlenum',
    nameFieldIndex: 0,
    totalFields: 7,
    fieldMappings: {},
    portMappings: [
      { portId: 'air-outlet', fieldIndex: 3, fieldName: 'Outlet Node Name' },
      { portId: 'inlet-1', fieldIndex: 4, fieldName: 'Inlet 1 Node Name' },
      { portId: 'inlet-2', fieldIndex: 5, fieldName: 'Inlet 2 Node Name' },
      { portId: 'inlet-3', fieldIndex: 6, fieldName: 'Inlet 3 Node Name' },
    ],
  },

  // ============================================
  // PLANT EQUIPMENT
  // ============================================
  {
    componentType: 'chiller-electric',
    idfClass: 'Chiller:Electric:EIR',
    nameFieldIndex: 0,
    totalFields: 24,
    fieldMappings: {
      'Reference Capacity': { fieldIndex: 1, defaultValue: 'autosize' },
      'Reference COP': { fieldIndex: 2, defaultValue: '5.5' },
      'Reference Leaving Chilled Water Temperature': { fieldIndex: 3, defaultValue: '6.67' },
      'Reference Entering Condenser Fluid Temperature': { fieldIndex: 4, defaultValue: '29.4' },
    },
    portMappings: [
      { portId: 'chw-inlet', fieldIndex: 7, fieldName: 'Chilled Water Inlet Node Name' },
      { portId: 'chw-outlet', fieldIndex: 8, fieldName: 'Chilled Water Outlet Node Name' },
      { portId: 'cw-inlet', fieldIndex: 9, fieldName: 'Condenser Inlet Node Name' },
      { portId: 'cw-outlet', fieldIndex: 10, fieldName: 'Condenser Outlet Node Name' },
    ],
  },
  {
    componentType: 'boiler-hot-water',
    idfClass: 'Boiler:HotWater',
    nameFieldIndex: 0,
    totalFields: 11,
    fieldMappings: {
      'Fuel Type': { fieldIndex: 1, defaultValue: 'NaturalGas' },
      'Nominal Capacity': { fieldIndex: 2, defaultValue: 'autosize' },
      'Nominal Thermal Efficiency': { fieldIndex: 3, defaultValue: '0.8' },
      'Design Water Flow Rate': { fieldIndex: 5, defaultValue: 'autosize' },
    },
    portMappings: [
      { portId: 'water-inlet', fieldIndex: 6, fieldName: 'Boiler Water Inlet Node Name' },
      { portId: 'water-outlet', fieldIndex: 7, fieldName: 'Boiler Water Outlet Node Name' },
    ],
  },
  {
    componentType: 'cooling-tower',
    idfClass: 'CoolingTower:SingleSpeed',
    nameFieldIndex: 0,
    totalFields: 16,
    fieldMappings: {
      'Design Water Flow Rate': { fieldIndex: 3, defaultValue: 'autosize' },
      'Design Air Flow Rate': { fieldIndex: 5, defaultValue: 'autosize' },
      'Design Fan Power': { fieldIndex: 6, defaultValue: 'autosize' },
    },
    portMappings: [
      { portId: 'water-inlet', fieldIndex: 1, fieldName: 'Water Inlet Node Name' },
      { portId: 'water-outlet', fieldIndex: 2, fieldName: 'Water Outlet Node Name' },
    ],
  },
  {
    componentType: 'pump-variable-speed',
    idfClass: 'Pump:VariableSpeed',
    nameFieldIndex: 0,
    totalFields: 19,
    fieldMappings: {
      'Design Maximum Flow Rate': { fieldIndex: 3, defaultValue: 'autosize' },
      'Design Pump Head': { fieldIndex: 4, defaultValue: '179352' },
      'Design Power Consumption': { fieldIndex: 5, defaultValue: 'autosize' },
      'Motor Efficiency': { fieldIndex: 6, defaultValue: '0.9' },
    },
    portMappings: [
      { portId: 'water-inlet', fieldIndex: 1, fieldName: 'Inlet Node Name' },
      { portId: 'water-outlet', fieldIndex: 2, fieldName: 'Outlet Node Name' },
    ],
  },
  {
    componentType: 'pump-constant-speed',
    idfClass: 'Pump:ConstantSpeed',
    nameFieldIndex: 0,
    totalFields: 14,
    fieldMappings: {
      'Design Flow Rate': { fieldIndex: 3, defaultValue: 'autosize' },
      'Design Pump Head': { fieldIndex: 4, defaultValue: '179352' },
      'Design Power Consumption': { fieldIndex: 5, defaultValue: 'autosize' },
      'Motor Efficiency': { fieldIndex: 6, defaultValue: '0.9' },
    },
    portMappings: [
      { portId: 'water-inlet', fieldIndex: 1, fieldName: 'Inlet Node Name' },
      { portId: 'water-outlet', fieldIndex: 2, fieldName: 'Outlet Node Name' },
    ],
  },
];

/**
 * Lookup mapping by component type
 */
export const MAPPING_BY_TYPE = new Map<string, ComponentIDFMapping>(
  COMPONENT_IDF_MAPPINGS.map((m) => [m.componentType, m])
);

/**
 * Lookup mapping by IDF class
 */
export const MAPPING_BY_IDF_CLASS = new Map<string, ComponentIDFMapping>(
  COMPONENT_IDF_MAPPINGS.map((m) => [m.idfClass, m])
);

/**
 * Get field mapping for a component type
 */
export function getComponentMapping(componentType: string): ComponentIDFMapping | undefined {
  return MAPPING_BY_TYPE.get(componentType);
}

/**
 * Get field mapping by IDF class name
 */
export function getMappingByIDFClass(idfClass: string): ComponentIDFMapping | undefined {
  return MAPPING_BY_IDF_CLASS.get(idfClass);
}

/**
 * Check if an IDF class is mappable to an HVAC component
 */
export function isHVACClass(idfClass: string): boolean {
  return MAPPING_BY_IDF_CLASS.has(idfClass);
}
